/**
 * Views of items in the Flux Agent media store: photo thumbnails in the conversation and the
 * player on spoken-clip and sound-cue cards. Both load the owner-only bytes through a
 * credentialed `blob:` URL ({@link loadMediaObjectUrl}).
 *
 * @module apps/web/src/components/flux-agent/FluxAgentMediaViews
 */

import { useEffect, useState } from 'react';
import { Download, ImageOff, Loader2 } from 'lucide-react';
import type { FluxAgentMedia } from '@/lib/fluxAgentApi';
import { downloadName, formatDuration, loadMediaObjectUrl } from '@/lib/fluxAgentPhoto';
import { cn } from '@/lib/utils';
import { useUserStore } from '@/stores/userStore';

/** Resolve a media path to a `blob:` URL; `undefined` while loading, `null` when unavailable. */
function useMediaUrl(path: string | undefined): string | null | undefined {
  const userId = useUserStore(state => state.userId);
  const key = `${userId ?? ''}:${path ?? ''}`;
  const [resolved, setResolved] = useState<{ key: string; url: string | null } | null>(null);
  useEffect(() => {
    const controller = new AbortController();
    let ownedUrl: string | null = null;
    if (!path || !userId) {
      setResolved({ key, url: null });
      return;
    }
    if (path.startsWith('blob:')) {
      setResolved({ key, url: path });
      return;
    }
    void loadMediaObjectUrl(path, fetch, controller.signal).then(url => {
      if (controller.signal.aborted) {
        if (url) URL.revokeObjectURL(url);
        return;
      }
      ownedUrl = url;
      setResolved({ key, url });
    });
    return () => {
      controller.abort();
      if (ownedUrl) URL.revokeObjectURL(ownedUrl);
    };
  }, [path, userId, key]);
  return resolved?.key === key ? resolved.url : undefined;
}

/** Inputs for {@link FluxAgentPhotoThumb}. */
export interface FluxAgentPhotoThumbProps {
  /** Media path from the server, or a local `blob:` preview. */
  src: string | undefined;
  /** Alternative text for the photo. */
  alt: string;
  /** Extra classes for the square frame; defaults to 4rem. */
  className?: string;
}

/** A square photo thumbnail that loads the owner-only bytes; a placeholder while loading or missing. */
export function FluxAgentPhotoThumb({ src, alt, className }: FluxAgentPhotoThumbProps) {
  const url = useMediaUrl(src);
  return (
    <span
      className={cn(
        'bg-muted flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-lg',
        className
      )}
    >
      {url ? (
        <img src={url} alt={alt} className="h-full w-full object-cover" />
      ) : url === undefined ? (
        <Loader2
          className="text-muted-foreground h-4 w-4 animate-spin"
          aria-label="Loading photo"
        />
      ) : (
        <ImageOff className="text-muted-foreground h-4 w-4" aria-label="Photo unavailable" />
      )}
    </span>
  );
}

/** Inputs for {@link FluxAgentSoundPlayer}. */
export interface FluxAgentSoundPlayerProps {
  /** The stored clip or cue. */
  media: FluxAgentMedia;
}

/** An audio player with the clip's length and a download link, for a `sound` action card. */
export function FluxAgentSoundPlayer({ media }: FluxAgentSoundPlayerProps) {
  const url = useMediaUrl(media.url);
  const duration = formatDuration(media.durationMs);
  return (
    <div className="mt-2 space-y-1.5" data-testid="flux-agent-sound-player">
      {url ? (
        <audio controls preload="metadata" src={url} className="h-9 w-full max-w-sm">
          <track kind="captions" />
        </audio>
      ) : url === undefined ? (
        <Loader2
          className="text-muted-foreground h-4 w-4 animate-spin"
          aria-label="Loading audio"
        />
      ) : (
        <p className="text-muted-foreground text-xs">This clip is no longer available.</p>
      )}
      <div className="text-muted-foreground flex items-center gap-3 text-xs">
        {duration && <span className="tabular-nums">{duration}</span>}
        {url && (
          <a
            href={url}
            download={downloadName(media.label, media.mimeType)}
            className="hover:text-foreground inline-flex items-center gap-1 underline-offset-2 hover:underline"
          >
            <Download className="h-3 w-3" aria-hidden="true" /> Download
          </a>
        )}
      </div>
    </div>
  );
}
